import React, { useState } from 'react';
import {
  Box,
  Paper,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Typography,
  Divider,
  Grid,
  Chip,
  InputAdornment,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Slider,
  Button,
  Alert,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tabs,
  Tab,
  Popover
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Lock as LockIcon,
  Public as PublicIcon,
  Link as LinkIcon,
  Upload as UploadIcon,
  Image as ImageIcon,
  Clear as ClearIcon,
  ColorLens as ColorLensIcon,
  FormatBold as FormatBoldIcon,
  FormatItalic as FormatItalicIcon,
  Rotate90DegreesCcw as RotateIcon,
  GridOn as GridIcon,
  FormatSize as FormatSizeIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

const PDFSettings = ({
  config,
  isPublic,
  tags,
  slug,
  onConfigChange,
  onPublicChange,
  onSlugChange,
  onAddTag,
  onRemoveTag
}) => {
  const [newTag, setNewTag] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [uploadingWatermark, setUploadingWatermark] = useState(false);
  const [expandedPanels, setExpandedPanels] = useState(['general']);
  const [colorPickerAnchor, setColorPickerAnchor] = useState(null);
  const [watermarkTab, setWatermarkTab] = useState(0);
  
  // Handle panel expansion
  const handlePanelChange = (panel) => (event, isExpanded) => {
    setExpandedPanels(prev => 
      isExpanded 
        ? [...prev, panel]
        : prev.filter(p => p !== panel)
    );
  };
  
  // Handle tag input
  const handleTagKeyPress = (e) => {
    if (e.key === 'Enter' && newTag.trim()) {
      e.preventDefault();
      onAddTag(newTag.trim());
      setNewTag('');
    }
  };
  
  // Generate slug from title
  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  // Handle watermark image upload
  const handleWatermarkImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!file.type.startsWith('image/')) {
      alert('Please upload an image file');
      return;
    }

    // Check file size (max 2MB for base64)
    if (file.size > 2 * 1024 * 1024) {
      alert('Image size should be less than 2MB');
      return;
    }

    setUploadingWatermark(true);
    
    try {
      // Convert to base64
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const base64Image = e.target.result;
        
        // Optionally compress the image if it's large
        if (file.size > 500 * 1024) { // If larger than 500KB, compress it
          compressImage(base64Image, (compressedImage) => {
            onConfigChange('watermark', {
              ...config.watermark,
              image: compressedImage
            });
            setUploadingWatermark(false);
          });
        } else {
          // Use original if small enough
          onConfigChange('watermark', {
            ...config.watermark,
            image: base64Image
          });
          setUploadingWatermark(false);
        }
      };
      
      reader.onerror = (error) => {
        console.error('Error reading file:', error);
        alert('Failed to read image file. Please try again.');
        setUploadingWatermark(false);
      };
      
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing watermark image:', error);
      alert('Failed to process image. Please try again.');
      setUploadingWatermark(false);
    }
  };

  // Compress image using canvas
  const compressImage = (base64Image, callback) => {
    const img = new Image();
    img.onload = function() {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Calculate new dimensions (max 300px width/height for watermark)
      let width = img.width;
      let height = img.height;
      const maxSize = 300;
      
      if (width > height) {
        if (width > maxSize) {
          height = (height * maxSize) / width;
          width = maxSize;
        }
      } else {
        if (height > maxSize) {
          width = (width * maxSize) / height;
          height = maxSize;
        }
      }
      
      canvas.width = width;
      canvas.height = height;
      
      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convert to base64 with compression
      const compressedBase64 = canvas.toDataURL('image/jpeg', 0.8);
      callback(compressedBase64);
    };
    
    img.onerror = function() {
      console.error('Error loading image for compression');
      callback(base64Image); // Fallback to original
    };
    
    img.src = base64Image;
  };
  
  return (
    <Box 
      component="form" 
      sx={{ p: 3, height: '100%', overflow: 'auto' }}
      autoComplete="off"
      onSubmit={(e) => e.preventDefault()}
    >
      {/* General Settings */}
      <Accordion 
        expanded={expandedPanels.includes('general')}
        onChange={handlePanelChange('general')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">General Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Public/Private Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={isPublic}
                    onChange={(e) => onPublicChange(e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isPublic ? <PublicIcon /> : <LockIcon />}
                    <Typography>
                      {isPublic ? 'Public (Anyone with link can view)' : 'Private (Requires authentication)'}
                    </Typography>
                  </Box>
                }
              />
            </Grid>
            
            {/* URL Slug - Only show when public */}
            {isPublic && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="URL Slug"
                  value={slug || ''}
                  onChange={(e) => onSlugChange(e.target.value)}
                  placeholder="my-document"
                  helperText="Custom URL: /docs/my-document"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  InputProps={{
                    autoComplete: 'off',
                    startAdornment: (
                      <InputAdornment position="start">
                        <LinkIcon />
                      </InputAdornment>
                    ),
                    endAdornment: slug && (
                      <Button
                        size="small"
                        onClick={() => onSlugChange(generateSlug(slug))}
                      >
                        Clean
                      </Button>
                    )
                  }}
                />
              </Grid>
            )}
            
            {/* Public Access Warning */}
            {isPublic && (
              <Grid item xs={12}>
                <Alert severity="warning">
                  This PDF will be accessible to anyone with the link. Consider adding password protection for sensitive content.
                </Alert>
              </Grid>
            )}
            
            {/* Tags */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Add Tags"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyPress={handleTagKeyPress}
                placeholder="Press Enter to add"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                InputProps={{
                  autoComplete: 'off',
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (newTag.trim()) {
                            onAddTag(newTag.trim());
                            setNewTag('');
                          }
                        }}
                      >
                        <AddIcon />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => onRemoveTag(tag)}
                    size="small"
                  />
                ))}
              </Box>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Page Settings */}
      <Accordion 
        expanded={expandedPanels.includes('page')}
        onChange={handlePanelChange('page')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Page Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* PDF Type */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>PDF Type</InputLabel>
                <Select
                  value={config.pdfType || 'simple'}
                  onChange={(e) => onConfigChange('pdfType', e.target.value)}
                >
                  <MenuItem value="simple">
                    <Box>
                      <Typography variant="body1">Simple PDF</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Standard PDF with static content
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="interactive">
                    <Box>
                      <Typography variant="body1">Interactive PDF</Typography>
                      <Typography variant="caption" color="text.secondary">
                        PDF with fillable form fields (input fields, checkboxes, etc.)
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>
              {config.pdfType === 'interactive' && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Interactive PDFs allow users to fill out forms directly in the PDF. Input fields, checkboxes, and other form elements will be interactive.
                </Alert>
              )}
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Page Size</InputLabel>
                <Select
                  value={config.pageSize || 'A4'}
                  onChange={(e) => onConfigChange('pageSize', e.target.value)}
                >
                  <MenuItem value="A4">A4</MenuItem>
                  <MenuItem value="Letter">Letter</MenuItem>
                  <MenuItem value="Legal">Legal</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel>Orientation</InputLabel>
                <Select
                  value={config.orientation || 'portrait'}
                  onChange={(e) => onConfigChange('orientation', e.target.value)}
                >
                  <MenuItem value="portrait">Portrait</MenuItem>
                  <MenuItem value="landscape">Landscape</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <Typography gutterBottom>Margins (mm)</Typography>
              <Grid container spacing={2}>
                {['top', 'right', 'bottom', 'left'].map((side) => (
                  <Grid item xs={3} key={side}>
                    <TextField
                      fullWidth
                      label={side.charAt(0).toUpperCase() + side.slice(1)}
                      type="number"
                      value={config.margins?.[side] || 20}
                      onChange={(e) => onConfigChange('margins', {
                        ...config.margins,
                        [side]: parseInt(e.target.value) || 0
                      })}
                      InputProps={{
                        inputProps: { min: 0, max: 50 }
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.pageNumbers || false}
                    onChange={(e) => onConfigChange('pageNumbers', e.target.checked)}
                  />
                }
                label="Show page numbers"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config?.headerFooter?.enabled || false}
                    onChange={(e) => onConfigChange('headerFooter', {
                      ...config.headerFooter,
                      enabled: e.target.checked
                    })}
                  />
                }
                label="Include header and footer"
              />
            </Grid>
            
            {config?.headerFooter?.enabled && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Header Settings</Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Header Left"
                    value={config.headerFooter?.header?.left || ''}
                    onChange={(e) => onConfigChange('headerFooter', {
                      ...config.headerFooter,
                      header: {
                        ...config.headerFooter?.header,
                        left: e.target.value
                      }
                    })}
                    placeholder="e.g., Document Title"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Header Center"
                    value={config.headerFooter?.header?.center || ''}
                    onChange={(e) => onConfigChange('headerFooter', {
                      ...config.headerFooter,
                      header: {
                        ...config.headerFooter?.header,
                        center: e.target.value
                      }
                    })}
                    placeholder="e.g., Company Name"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Header Right"
                    value={config.headerFooter?.header?.right || ''}
                    onChange={(e) => onConfigChange('headerFooter', {
                      ...config.headerFooter,
                      header: {
                        ...config.headerFooter?.header,
                        right: e.target.value
                      }
                    })}
                    placeholder="e.g., Date"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>Footer Settings</Typography>
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Footer Left"
                    value={config.headerFooter?.footer?.left || ''}
                    onChange={(e) => onConfigChange('headerFooter', {
                      ...config.headerFooter,
                      footer: {
                        ...config.headerFooter?.footer,
                        left: e.target.value
                      }
                    })}
                    placeholder="e.g., Page Number"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Footer Center"
                    value={config.headerFooter?.footer?.center || ''}
                    onChange={(e) => onConfigChange('headerFooter', {
                      ...config.headerFooter,
                      footer: {
                        ...config.headerFooter?.footer,
                        center: e.target.value
                      }
                    })}
                    placeholder="e.g., Copyright"
                  />
                </Grid>
                <Grid item xs={4}>
                  <TextField
                    fullWidth
                    label="Footer Right"
                    value={config.headerFooter?.footer?.right || ''}
                    onChange={(e) => onConfigChange('headerFooter', {
                      ...config.headerFooter,
                      footer: {
                        ...config.headerFooter?.footer,
                        right: e.target.value
                      }
                    })}
                    placeholder="e.g., Website"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Security Settings */}
      <Accordion 
        expanded={expandedPanels.includes('security')}
        onChange={handlePanelChange('security')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Security</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            {/* Security Info */}
            {!isPublic && (
              <Grid item xs={12}>
                <Alert severity="info">
                  Password protection is available for both public and private PDFs. Private PDFs require authentication first, then password if set.
                </Alert>
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Password Protection"
                type={showPassword ? 'text' : 'password'}
                value={config.security?.password || ''}
                onChange={(e) => onConfigChange('security', {
                  ...config.security,
                  password: e.target.value
                })}
                placeholder="Leave empty for no password"
                autoComplete="new-password"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                InputProps={{
                  autoComplete: 'new-password',
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Expiration Date"
                  value={config.security?.expiresAt || null}
                  onChange={(value) => onConfigChange('security', {
                    ...config.security,
                    expiresAt: value
                  })}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* Watermark Settings */}
      <Accordion 
        expanded={expandedPanels.includes('watermark')}
        onChange={handlePanelChange('watermark')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Watermark</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.watermark?.enabled || false}
                    onChange={(e) => onConfigChange('watermark', {
                      ...config.watermark,
                      enabled: e.target.checked
                    })}
                  />
                }
                label="Enable watermark"
              />
            </Grid>
            
            {config.watermark?.enabled && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Watermark Text"
                    value={config.watermark?.text || ''}
                    onChange={(e) => onConfigChange('watermark', {
                      ...config.watermark,
                      text: e.target.value
                    })}
                    placeholder={isPublic ? "SAMPLE" : "CONFIDENTIAL"}
                    helperText={config.watermark?.image ? "Text will be ignored when image is set" : "Text to display as watermark"}
                    disabled={!!config.watermark?.image}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Watermark Image"
                    value={config.watermark?.image?.startsWith('data:') ? 'Uploaded image' : (config.watermark?.image || '')}
                    onChange={(e) => onConfigChange('watermark', {
                      ...config.watermark,
                      image: e.target.value
                    })}
                    placeholder="Enter image URL or upload a file"
                    helperText={
                      config.watermark?.image?.startsWith('data:') 
                        ? "Using uploaded image (click X to remove)" 
                        : config.watermark?.image 
                          ? "Using image URL (click X to remove)" 
                          : "Optional: Use an image instead of text"
                    }
                    disabled={config.watermark?.image?.startsWith('data:')}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck="false"
                    InputProps={{
                      startAdornment: config.watermark?.image && (
                        <InputAdornment position="start">
                          {config.watermark.image.startsWith('data:') ? (
                            <Box
                              component="img"
                              src={config.watermark.image}
                              alt="Watermark preview"
                              sx={{ 
                                width: 24, 
                                height: 24, 
                                objectFit: 'contain',
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1
                              }}
                            />
                          ) : (
                            <ImageIcon />
                          )}
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {config.watermark?.image && (
                            <IconButton
                              onClick={() => onConfigChange('watermark', {
                                ...config.watermark,
                                image: ''
                              })}
                              title="Clear image"
                              size="small"
                            >
                              <ClearIcon />
                            </IconButton>
                          )}
                          <input
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="watermark-image-upload"
                            type="file"
                            onChange={handleWatermarkImageUpload}
                          />
                          <label htmlFor="watermark-image-upload">
                            <IconButton
                              component="span"
                              disabled={uploadingWatermark}
                              title="Upload image"
                            >
                              {uploadingWatermark ? (
                                <CircularProgress size={24} />
                              ) : (
                                <UploadIcon />
                              )}
                            </IconButton>
                          </label>
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                
                {/* Watermark Tabs for better organization */}
                <Grid item xs={12}>
                  <Tabs 
                    value={watermarkTab} 
                    onChange={(e, v) => setWatermarkTab(v)}
                    variant="fullWidth"
                    sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
                  >
                    <Tab label="Basic" />
                    <Tab label="Style" />
                    <Tab label="Advanced" />
                  </Tabs>
                </Grid>

                {/* Basic Tab */}
                {watermarkTab === 0 && (
                  <>
                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth disabled={config.watermark?.pattern?.enabled}>
                        <InputLabel>Position</InputLabel>
                        <Select
                          value={config.watermark?.position || 'center'}
                          onChange={(e) => onConfigChange('watermark', {
                            ...config.watermark,
                            position: e.target.value
                          })}
                        >
                          <MenuItem value="center">Center</MenuItem>
                          <MenuItem value="diagonal">Diagonal</MenuItem>
                          <MenuItem value="top-left">Top Left</MenuItem>
                          <MenuItem value="top-right">Top Right</MenuItem>
                          <MenuItem value="bottom-left">Bottom Left</MenuItem>
                          <MenuItem value="bottom-right">Bottom Right</MenuItem>
                          <MenuItem value="custom">Custom Position</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    {/* Custom Position Sliders */}
                    {config.watermark?.position === 'custom' && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Typography gutterBottom>
                            X Position: {config.watermark?.customPosition?.x || 50}%
                          </Typography>
                          <Slider
                            value={config.watermark?.customPosition?.x || 50}
                            onChange={(e, value) => onConfigChange('watermark', {
                              ...config.watermark,
                              customPosition: {
                                ...config.watermark?.customPosition,
                                x: value
                              }
                            })}
                            min={0}
                            max={100}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${v}%`}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Typography gutterBottom>
                            Y Position: {config.watermark?.customPosition?.y || 50}%
                          </Typography>
                          <Slider
                            value={config.watermark?.customPosition?.y || 50}
                            onChange={(e, value) => onConfigChange('watermark', {
                              ...config.watermark,
                              customPosition: {
                                ...config.watermark?.customPosition,
                                y: value
                              }
                            })}
                            min={0}
                            max={100}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${v}%`}
                          />
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom>
                        Opacity: {Math.round((config.watermark?.opacity || 0.3) * 100)}%
                      </Typography>
                      <Slider
                        value={config.watermark?.opacity || 0.3}
                        onChange={(e, value) => onConfigChange('watermark', {
                          ...config.watermark,
                          opacity: value
                        })}
                        min={0}
                        max={1}
                        step={0.05}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <Typography gutterBottom>
                        Rotation: {config.watermark?.rotation !== undefined ? config.watermark.rotation : 0}°
                      </Typography>
                      <Slider
                        value={config.watermark?.rotation !== undefined ? config.watermark.rotation : 0}
                        onChange={(e, value) => onConfigChange('watermark', {
                          ...config.watermark,
                          rotation: value
                        })}
                        min={-180}
                        max={180}
                        step={15}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v}°`}
                        marks={[
                          { value: -180, label: '-180°' },
                          { value: -90, label: '-90°' },
                          { value: 0, label: '0°' },
                          { value: 90, label: '90°' },
                          { value: 180, label: '180°' }
                        ]}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.watermark?.downloadOnly || false}
                            onChange={(e) => onConfigChange('watermark', {
                              ...config.watermark,
                              downloadOnly: e.target.checked
                            })}
                          />
                        }
                        label="Show watermark on download only"
                        helperText="Watermark will not appear in the web view"
                      />
                    </Grid>
                  </>
                )}

                {/* Style Tab */}
                {watermarkTab === 1 && (
                  <>
                    {/* Text Size (for text watermarks) */}
                    {!config.watermark?.image && (
                      <Grid item xs={12} md={6}>
                        <Typography gutterBottom>
                          Text Size: {config.watermark?.textSize || 60}px
                        </Typography>
                        <Slider
                          value={config.watermark?.textSize || 60}
                          onChange={(e, value) => onConfigChange('watermark', {
                            ...config.watermark,
                            textSize: value
                          })}
                          min={10}
                          max={200}
                          step={5}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(v) => `${v}px`}
                        />
                      </Grid>
                    )}

                    {/* Image Scale (for image watermarks) */}
                    {config.watermark?.image && (
                      <Grid item xs={12} md={6}>
                        <Typography gutterBottom>
                          Image Scale: {((config.watermark?.imageScale || 0.5) * 100).toFixed(0)}%
                        </Typography>
                        <Slider
                          value={config.watermark?.imageScale || 0.5}
                          onChange={(e, value) => onConfigChange('watermark', {
                            ...config.watermark,
                            imageScale: value
                          })}
                          min={0.1}
                          max={2}
                          step={0.1}
                          valueLabelDisplay="auto"
                          valueLabelFormat={(v) => `${(v * 100).toFixed(0)}%`}
                        />
                      </Grid>
                    )}

                    {/* Color Picker (for text watermarks) */}
                    {!config.watermark?.image && (
                      <Grid item xs={12} md={6}>
                        <TextField
                          fullWidth
                          label="Color"
                          value={config.watermark?.color || 'rgba(0, 0, 0, 0.3)'}
                          onClick={(e) => setColorPickerAnchor(e.currentTarget)}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Box
                                  sx={{
                                    width: 24,
                                    height: 24,
                                    backgroundColor: config.watermark?.color || 'rgba(0, 0, 0, 0.3)',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    borderRadius: 1,
                                    cursor: 'pointer'
                                  }}
                                />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <ColorLensIcon />
                              </InputAdornment>
                            )
                          }}
                          readOnly
                        />
                        <Popover
                          open={Boolean(colorPickerAnchor)}
                          anchorEl={colorPickerAnchor}
                          onClose={() => setColorPickerAnchor(null)}
                          anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'left',
                          }}
                        >
                          <Box sx={{ p: 2 }}>
                            <input
                              type="color"
                              value={config.watermark?.color?.split('(')[0] === 'rgba' ? 
                                '#' + config.watermark.color.match(/\d+/g).slice(0, 3).map(x => (+x).toString(16).padStart(2, '0')).join('') :
                                config.watermark?.color || '#000000'
                              }
                              onChange={(e) => {
                                const hex = e.target.value;
                                const r = parseInt(hex.slice(1, 3), 16);
                                const g = parseInt(hex.slice(3, 5), 16);
                                const b = parseInt(hex.slice(5, 7), 16);
                                const opacity = config.watermark?.opacity || 0.3;
                                onConfigChange('watermark', {
                                  ...config.watermark,
                                  color: `rgba(${r}, ${g}, ${b}, ${opacity})`
                                });
                              }}
                              style={{ width: 200, height: 40 }}
                            />
                          </Box>
                        </Popover>
                      </Grid>
                    )}

                    {/* Font Options (for text watermarks) */}
                    {!config.watermark?.image && (
                      <>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Font Family</InputLabel>
                            <Select
                              value={config.watermark?.fontFamily || 'Arial'}
                              onChange={(e) => onConfigChange('watermark', {
                                ...config.watermark,
                                fontFamily: e.target.value
                              })}
                            >
                              <MenuItem value="Arial">Arial</MenuItem>
                              <MenuItem value="Helvetica">Helvetica</MenuItem>
                              <MenuItem value="Times New Roman">Times New Roman</MenuItem>
                              <MenuItem value="Georgia">Georgia</MenuItem>
                              <MenuItem value="Courier New">Courier New</MenuItem>
                              <MenuItem value="Verdana">Verdana</MenuItem>
                              <MenuItem value="Impact">Impact</MenuItem>
                              <MenuItem value="Comic Sans MS">Comic Sans MS</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <ToggleButtonGroup
                            value={[
                              config.watermark?.fontStyle?.bold ? 'bold' : '',
                              config.watermark?.fontStyle?.italic ? 'italic' : ''
                            ].filter(Boolean)}
                            onChange={(e, values) => onConfigChange('watermark', {
                              ...config.watermark,
                              fontStyle: {
                                bold: values.includes('bold'),
                                italic: values.includes('italic')
                              }
                            })}
                            aria-label="text formatting"
                          >
                            <ToggleButton value="bold" aria-label="bold">
                              <FormatBoldIcon />
                            </ToggleButton>
                            <ToggleButton value="italic" aria-label="italic">
                              <FormatItalicIcon />
                            </ToggleButton>
                          </ToggleButtonGroup>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Text Transform</InputLabel>
                            <Select
                              value={config.watermark?.textTransform || 'none'}
                              onChange={(e) => onConfigChange('watermark', {
                                ...config.watermark,
                                textTransform: e.target.value
                              })}
                            >
                              <MenuItem value="none">None</MenuItem>
                              <MenuItem value="uppercase">UPPERCASE</MenuItem>
                              <MenuItem value="lowercase">lowercase</MenuItem>
                              <MenuItem value="capitalize">Capitalize</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    <Grid item xs={12} md={6}>
                      <FormControl fullWidth>
                        <InputLabel>Blend Mode</InputLabel>
                        <Select
                          value={config.watermark?.blendMode || 'normal'}
                          onChange={(e) => onConfigChange('watermark', {
                            ...config.watermark,
                            blendMode: e.target.value
                          })}
                        >
                          <MenuItem value="normal">Normal</MenuItem>
                          <MenuItem value="multiply">Multiply</MenuItem>
                          <MenuItem value="screen">Screen</MenuItem>
                          <MenuItem value="overlay">Overlay</MenuItem>
                          <MenuItem value="darken">Darken</MenuItem>
                          <MenuItem value="lighten">Lighten</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {/* Advanced Tab */}
                {watermarkTab === 2 && (
                  <>
                    {/* Pattern Mode */}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.watermark?.pattern?.enabled || false}
                            onChange={(e) => onConfigChange('watermark', {
                              ...config.watermark,
                              pattern: {
                                ...config.watermark?.pattern,
                                enabled: e.target.checked
                              }
                            })}
                          />
                        }
                        label="Pattern Mode"
                        helperText="Repeat watermark across the page (ignores position settings)"
                      />
                    </Grid>

                    {config.watermark?.pattern?.enabled && (
                      <>
                        <Grid item xs={12} md={6}>
                          <Typography gutterBottom>
                            Horizontal Spacing: {config.watermark?.pattern?.spacing?.horizontal || 150}px
                          </Typography>
                          <Slider
                            value={config.watermark?.pattern?.spacing?.horizontal || 150}
                            onChange={(e, value) => onConfigChange('watermark', {
                              ...config.watermark,
                              pattern: {
                                ...config.watermark?.pattern,
                                spacing: {
                                  ...config.watermark?.pattern?.spacing,
                                  horizontal: value
                                }
                              }
                            })}
                            min={0}
                            max={500}
                            step={10}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${v}px`}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <Typography gutterBottom>
                            Vertical Spacing: {config.watermark?.pattern?.spacing?.vertical || 150}px
                          </Typography>
                          <Slider
                            value={config.watermark?.pattern?.spacing?.vertical || 150}
                            onChange={(e, value) => onConfigChange('watermark', {
                              ...config.watermark,
                              pattern: {
                                ...config.watermark?.pattern,
                                spacing: {
                                  ...config.watermark?.pattern?.spacing,
                                  vertical: value
                                }
                              }
                            })}
                            min={0}
                            max={500}
                            step={10}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(v) => `${v}px`}
                          />
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth>
                            <InputLabel>Pattern Density</InputLabel>
                            <Select
                              value={config.watermark?.pattern?.density || 'medium'}
                              onChange={(e) => onConfigChange('watermark', {
                                ...config.watermark,
                                pattern: {
                                  ...config.watermark?.pattern,
                                  density: e.target.value
                                }
                              })}
                            >
                              <MenuItem value="low">Low (~25%)</MenuItem>
                              <MenuItem value="medium">Medium (~50%)</MenuItem>
                              <MenuItem value="high">High (~75%)</MenuItem>
                              <MenuItem value="maximum">Maximum (~100%)</MenuItem>
                            </Select>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                              Controls how densely the pattern fills the page
                            </Typography>
                          </FormControl>
                        </Grid>
                      </>
                    )}

                    {/* Border/Outline (for text watermarks) */}
                    {!config.watermark?.image && (
                      <>
                        <Grid item xs={12}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={config.watermark?.border?.enabled || false}
                                onChange={(e) => onConfigChange('watermark', {
                                  ...config.watermark,
                                  border: {
                                    ...config.watermark?.border,
                                    enabled: e.target.checked
                                  }
                                })}
                              />
                            }
                            label="Text Border/Outline"
                          />
                        </Grid>

                        {config.watermark?.border?.enabled && (
                          <>
                            <Grid item xs={12} md={6}>
                              <Typography gutterBottom>
                                Border Width: {config.watermark?.border?.width || 2}px
                              </Typography>
                              <Slider
                                value={config.watermark?.border?.width || 2}
                                onChange={(e, value) => onConfigChange('watermark', {
                                  ...config.watermark,
                                  border: {
                                    ...config.watermark?.border,
                                    width: value
                                  }
                                })}
                                min={1}
                                max={10}
                                step={1}
                                valueLabelDisplay="auto"
                                valueLabelFormat={(v) => `${v}px`}
                              />
                            </Grid>

                            <Grid item xs={12} md={6}>
                              <TextField
                                fullWidth
                                label="Border Color"
                                type="color"
                                value={config.watermark?.border?.color || '#ffffff'}
                                onChange={(e) => onConfigChange('watermark', {
                                  ...config.watermark,
                                  border: {
                                    ...config.watermark?.border,
                                    color: e.target.value
                                  }
                                })}
                                InputLabelProps={{ shrink: true }}
                              />
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
      
      {/* SEO Settings - Only show when public */}
      {isPublic && (
        <Accordion 
          expanded={expandedPanels.includes('seo')}
          onChange={handlePanelChange('seo')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">SEO & Metadata</Typography>
          </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Meta Description"
                value={config.seo?.metaDescription || ''}
                onChange={(e) => onConfigChange('seo', {
                  ...config.seo,
                  metaDescription: e.target.value
                })}
                placeholder="Brief description for search engines"
                inputProps={{ maxLength: 160 }}
                helperText={`${(config.seo?.metaDescription || '').length}/160 characters`}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="true"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Open Graph Image URL"
                value={config.seo?.ogImage || ''}
                onChange={(e) => onConfigChange('seo', {
                  ...config.seo,
                  ogImage: e.target.value
                })}
                placeholder="https://example.com/image.jpg"
                helperText="Image for social media previews (recommended: 1200x630px)"
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.seo?.indexable !== false}
                    onChange={(e) => onConfigChange('seo', {
                      ...config.seo,
                      indexable: e.target.checked
                    })}
                  />
                }
                label="Allow search engines to index this PDF"
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
      )}
      
      {/* Preview Settings */}
      <Accordion 
        expanded={expandedPanels.includes('preview')}
        onChange={handlePanelChange('preview')}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6">Editor Settings</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.preview?.autoRefresh !== false}
                    onChange={(e) => onConfigChange('preview', {
                      ...config.preview,
                      autoRefresh: e.target.checked
                    })}
                  />
                }
                label="Auto-refresh preview"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={config.jsEnabled || false}
                    onChange={(e) => onConfigChange('jsEnabled', e.target.checked)}
                  />
                }
                label="Enable custom JavaScript (Advanced)"
              />
              {config.jsEnabled && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  Custom JavaScript will be sandboxed for security
                </Alert>
              )}
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PDFSettings;